/**
 * ============================================================================
 * PolicyFormDialog — Phase 6a frontend (T-P6a-W3-01)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: create-or-edit modal for a retention policy. New policies always
 *          start in PENDING; a separate dual-sign activation flow promotes
 *          them to ACTIVE.
 *
 * A11Y:
 *   - role="dialog" + aria-modal + aria-labelledby + aria-describedby.
 *   - Initial focus → first field; closing returns focus to invoker (caller's
 *     responsibility — we cooperate via onClose only).
 *   - Required fields marked with both * glyph (aria-hidden) and validation.
 * ============================================================================ */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from '../AuthorWorkbench/NewDocumentForm.module.css';
import {
  useCreatePolicyMutation,
  type CreatePolicyInput,
} from './useCreatePolicyMutation';
import type {
  DataClass,
  RetentionAnchor,
  RetentionFormula,
  RetentionPolicy,
} from './usePoliciesQuery';

const FORMULA_OPTIONS: RetentionFormula[] = [
  'FIXED_YEARS',
  'YEARS_AFTER_PRODUCT_DISCONTINUATION',
  'YEARS_AFTER_EXPIRY',
  'INDEFINITE',
];

const DATA_CLASS_OPTIONS: DataClass[] = ['GMP', 'GLP', 'GCP', 'GDP', 'NON_GXP'];

const ANCHOR_OPTIONS: RetentionAnchor[] = [
  'EFFECTIVE_DATE',
  'SUPERSEDED_DATE',
  'PRODUCT_DISCONTINUATION',
  'BATCH_EXPIRY',
  'LAST_USE',
];

export interface PolicyFormDialogProps {
  initial?: RetentionPolicy;
  onClose: () => void;
  onSaved: () => void;
}

export function PolicyFormDialog({ initial, onClose, onSaved }: PolicyFormDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();

  const isNew = !initial;
  const createM = useCreatePolicyMutation();

  const [name, setName] = useState(initial?.name ?? '');
  const [docType, setDocType] = useState(initial?.doc_type_code ?? '');
  const [formula, setFormula] = useState<RetentionFormula>(
    initial?.formula ?? 'FIXED_YEARS',
  );
  const [years, setYears] = useState<string>(
    initial?.formula_years != null ? String(initial.formula_years) : '5',
  );
  const [dataClass, setDataClass] = useState<DataClass>(initial?.data_class ?? 'GMP');
  const [anchors, setAnchors] = useState<RetentionAnchor[]>(
    initial?.anchors ?? ['EFFECTIVE_DATE'],
  );
  const [error, setError] = useState<string | null>(null);

  const requiresYears = formula !== 'INDEFINITE';

  function toggleAnchor(a: RetentionAnchor) {
    setAnchors((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a],
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t('retention.matrix.errors.nameRequired', { defaultValue: 'Policy name is required.' }));
      return;
    }
    if (!docType.trim()) {
      setError(t('retention.matrix.errors.docTypeRequired', { defaultValue: 'Doc type is required.' }));
      return;
    }
    if (anchors.length === 0) {
      setError(t('retention.matrix.errors.anchorsRequired', { defaultValue: 'Pick at least one anchor.' }));
      return;
    }
    const yearsNum = requiresYears ? Number(years) : null;
    if (requiresYears && (!Number.isFinite(yearsNum!) || yearsNum! < 0)) {
      setError(t('retention.matrix.errors.yearsInvalid', { defaultValue: 'Years must be a non-negative number.' }));
      return;
    }

    const body: CreatePolicyInput = {
      name: name.trim(),
      doc_type_code: docType.trim(),
      formula,
      formula_years: yearsNum,
      data_class: dataClass,
      anchors,
    };

    try {
      await createM.mutateAsync(body);
      onSaved();
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError(t('retention.matrix.errors.serverFailed', { defaultValue: 'Server rejected the change.' }));
      }
    }
  }

  return (
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {isNew
              ? t('retention.matrix.dialog.newTitle', { defaultValue: 'New retention policy' })
              : t('retention.matrix.dialog.editTitle', { defaultValue: 'Edit retention policy' })}
          </h2>
          <p id={descId} className={styles.desc}>
            {t('retention.matrix.dialog.subtitle', {
              defaultValue:
                'New policies start in PENDING. Activation requires two distinct GMP-Critical signatures.',
            })}
          </p>
        </header>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={styles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="rp-name" className={styles.label}>
              {t('retention.matrix.fields.name', { defaultValue: 'Policy name' })}
              <span className={styles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="rp-name"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('retention.matrix.fields.namePlaceholder', {
                defaultValue: 'e.g., Batch records — 10 years post-expiry',
              })}
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="rp-doctype" className={styles.label}>
              {t('retention.matrix.fields.docType', { defaultValue: 'Doc type code' })}
              <span className={styles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="rp-doctype"
              className={styles.input}
              value={docType}
              onChange={(e) => setDocType(e.target.value.toUpperCase())}
              placeholder="BATCH_RECORD"
              disabled={!isNew}
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="rp-formula" className={styles.label}>
              {t('retention.matrix.fields.formula', { defaultValue: 'Formula' })}
            </label>
            <select
              id="rp-formula"
              className={styles.select}
              value={formula}
              onChange={(e) => setFormula(e.target.value as RetentionFormula)}
            >
              {FORMULA_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {t(`retention.matrix.formula.${f}`, { defaultValue: f })}
                </option>
              ))}
            </select>
          </div>

          {requiresYears && (
            <div className={styles.field}>
              <label htmlFor="rp-years" className={styles.label}>
                {t('retention.matrix.fields.years', { defaultValue: 'Retention years' })}
                <span className={styles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="rp-years"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                className={styles.input}
                value={years}
                onChange={(e) => setYears(e.target.value)}
                required
              />
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="rp-dataclass" className={styles.label}>
              {t('retention.matrix.fields.dataClass', { defaultValue: 'Data class' })}
            </label>
            <select
              id="rp-dataclass"
              className={styles.select}
              value={dataClass}
              onChange={(e) => setDataClass(e.target.value as DataClass)}
            >
              {DATA_CLASS_OPTIONS.map((dc) => (
                <option key={dc} value={dc}>
                  {dc}
                </option>
              ))}
            </select>
          </div>

          <fieldset className={styles.field}>
            <legend className={styles.label}>
              {t('retention.matrix.fields.anchors', { defaultValue: 'Retention anchors' })}
              <span className={styles.required} aria-hidden="true">*</span>
            </legend>
            {ANCHOR_OPTIONS.map((a) => (
              <label key={a} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={anchors.includes(a)}
                  onChange={() => toggleAnchor(a)}
                />
                <span>{t(`retention.matrix.anchor.${a}`, { defaultValue: a })}</span>
              </label>
            ))}
          </fieldset>

          <footer className={styles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={createM.isPending}>
              {isNew
                ? t('retention.matrix.actions.create', { defaultValue: 'Create policy' })
                : t('retention.matrix.actions.save', { defaultValue: 'Save changes' })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
